import { useEffect, useState } from "react";
import { Download, Share2, Smartphone, X } from "lucide-react";

const DISMISS_KEY = "bid_install_dismissed_at";
const DISMISS_DAYS = 14;

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isiOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
}

function recentlyDismissed() {
  const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
  if (!ts) return false;
  return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * InstallPWAButton + InstallPWAPrompt
 *
 * - Captures the browser's `beforeinstallprompt` event (Chrome/Edge/Android).
 * - On iOS Safari (no native prompt event), shows a custom hint with the
 *   share-then-"Add to Home Screen" instruction.
 * - Hides itself when already installed (display-mode: standalone) and when
 *   the user dismissed it in the last 14 days.
 */
export function useInstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setSupported(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    // Browsers without beforeinstallprompt (iOS Safari, some Firefox) still
    // support manual "Add to Home Screen" — surface a helpful instruction sheet
    // instead of hiding the button.
    setSupported(true);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const promptInstall = async () => {
    if (deferred) {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        localStorage.removeItem(DISMISS_KEY);
      }
      setDeferred(null);
      return choice?.outcome;
    }
    return null;
  };

  return { supported, deferred, promptInstall };
}

export function InstallPWAButton({ className = "", children, dataTestId = "install-pwa-btn" }) {
  const { supported, deferred, promptInstall } = useInstallPWA();
  const [showIOSSheet, setShowIOSSheet] = useState(false);

  if (!supported || isStandalone()) return null;

  const handleClick = async () => {
    if (deferred) {
      await promptInstall();
    } else {
      setShowIOSSheet(true);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={
          className ||
          "inline-flex items-center gap-2 px-5 py-3 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white text-xs uppercase tracking-[0.12em] font-medium transition-colors"
        }
        data-testid={dataTestId}
      >
        <Smartphone className="w-4 h-4" strokeWidth={1.75} />
        {children || "Install app"}
      </button>
      <InstallInstructionsSheet open={showIOSSheet} onClose={() => setShowIOSSheet(false)} />
    </>
  );
}

function InstallInstructionsSheet({ open, onClose }) {
  if (!open) return null;
  const ios = isiOS();
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="ios-install-sheet"
    >
      <div
        className="bg-white border border-zinc-200 w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="border-b border-zinc-200 px-5 py-4 flex items-center justify-between">
          <h2 className="text-base font-semibold tracking-tight">
            Install on {ios ? "iPhone" : isMobile ? "Android" : "your device"}
          </h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </header>
        <ol className="p-5 space-y-3 text-sm text-zinc-700">
          {ios ? (
            <>
              <Step n={1}>
                Tap the <Share2 className="inline w-4 h-4 -mt-0.5 mx-1" strokeWidth={1.75} />
                <span className="font-medium">Share</span> button at the bottom of Safari.
              </Step>
              <Step n={2}>
                Scroll down and tap <span className="font-medium">"Add to Home Screen"</span>.
              </Step>
              <Step n={3}>
                Tap <span className="font-medium">Add</span>. BreakItDown will open like a native app — no re-login.
              </Step>
            </>
          ) : isMobile ? (
            <>
              <Step n={1}>
                Tap the browser's <span className="font-medium">⋮ menu</span> in the top-right.
              </Step>
              <Step n={2}>
                Tap <span className="font-medium">"Install app"</span> or
                <span className="font-medium"> "Add to Home screen"</span>.
              </Step>
              <Step n={3}>
                Tap <span className="font-medium">Install</span>. Stays signed in for weeks.
              </Step>
            </>
          ) : (
            <>
              <Step n={1}>
                Click the install icon <Download className="inline w-4 h-4 -mt-0.5 mx-1" strokeWidth={1.75} /> in the address bar.
                (Chrome / Edge — appears once the page is fully loaded.)
              </Step>
              <Step n={2}>
                If you don't see it: open the browser menu and click
                <span className="font-medium"> "Install BreakItDown…"</span> or
                <span className="font-medium"> "Apps → Install this site"</span>.
              </Step>
              <Step n={3}>
                Confirm. Pin the resulting app to your taskbar or dock.
              </Step>
            </>
          )}
        </ol>
        <footer className="border-t border-zinc-200 px-5 py-3 flex justify-end">
          <button onClick={onClose} className="px-5 py-2 bg-zinc-900 hover:bg-[#002FA7] text-white text-xs font-medium uppercase tracking-[0.12em] transition-colors">
            Got it
          </button>
        </footer>
      </div>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="w-6 h-6 bg-zinc-900 text-white flex items-center justify-center text-xs font-mono shrink-0">{n}</span>
      <span>{children}</span>
    </li>
  );
}

// Keep alias for callers that imported the old name
function IOSInstallSheet(props) {
  return <InstallInstructionsSheet {...props} />;
}

/**
 * Floating bottom-right install banner shown on the homepage when the browser
 * supports installation. Dismissable; respects a 14-day cool-off.
 */
export function InstallPWAFloatingHint() {
  const { supported, deferred, promptInstall } = useInstallPWA();
  const [showIOSSheet, setShowIOSSheet] = useState(false);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    setDismissed(recentlyDismissed());
  }, []);

  if (!supported || isStandalone() || dismissed) return null;

  const handleInstall = async () => {
    if (deferred) {
      const outcome = await promptInstall();
      if (outcome === "dismissed") {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setDismissed(true);
      }
    } else {
      setShowIOSSheet(true);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <>
      <div
        className="fixed bottom-4 right-4 z-40 bg-white border border-zinc-200 shadow-lg max-w-xs"
        data-testid="install-pwa-hint"
      >
        <div className="px-4 py-3 flex items-start gap-3">
          <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center shrink-0 mt-0.5">
            <Download className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-zinc-900 leading-tight">Install BreakItDown</p>
            <p className="text-xs text-zinc-500 mt-0.5">One tap. Stays signed in. No app store.</p>
            <div className="flex items-center gap-3 mt-2">
              <button
                onClick={handleInstall}
                className="text-xs uppercase tracking-[0.12em] font-medium px-3 py-1.5 bg-zinc-900 hover:bg-[#002FA7] text-white transition-colors"
                data-testid="install-pwa-hint-install"
              >
                Install
              </button>
              <button
                onClick={handleDismiss}
                className="text-xs text-zinc-500 hover:text-zinc-900"
                data-testid="install-pwa-hint-dismiss"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
      <IOSInstallSheet open={showIOSSheet} onClose={() => setShowIOSSheet(false)} />
    </>
  );
}
